import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import BlazeAIBrand from "./BlazeAIBrand";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const STATS = [
  { number: "10+", label: "Years at AWS & enterprise cloud ops" },
  { number: "3", label: "Clouds supported from day one" },
  { number: "300+", label: "Built-in OPA policies" },
  { number: "100%", label: "Audit trail on every change" },
];

const VALUES = [
  {
    icon: ShieldOutlinedIcon,
    title: "Secure by default",
    body: "Least-privilege, audit-ready, and compliance-aligned — not as an afterthought.",
  },
  {
    icon: VisibilityOutlinedIcon,
    title: "Auditable by design",
    body: "Every change flows through reviewable, traceable workflows. Nothing ships blindly.",
  },
  {
    icon: CloudOutlinedIcon,
    title: "Multi-cloud from day one",
    body: "AWS, Azure, and GCP on the same canvas. No provider lock-in, no second-class citizens.",
  },
  {
    icon: AutoAwesomeOutlinedIcon,
    title: "Human + AI, together",
    body: (
      <>
        <BlazeAIBrand /> assists, humans approve. Automation with a seatbelt.
      </>
    ),
  },
  {
    icon: AssessmentOutlinedIcon,
    title: "Measured outcomes",
    body: "SLOs, telemetry, and cost signals — because if you can't measure it, you can't improve it.",
  },
  {
    icon: BoltOutlinedIcon,
    title: "Speed without panic",
    body: "Fast infrastructure changes shouldn't mean 3am incident calls. Guardrails make speed safe.",
  },
];

export default function AboutUs() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Scroll reveals
  const storyRef = useRef(null);
  const valuesRef = useRef(null);
  const tractionRef = useRef(null);
  const ctaRef = useRef(null);
  const [visible, setVisible] = useState({});

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReducedMotion(mq.matches);
    }
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible((p) => ({ ...p, [entry.target.dataset.key]: true }));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    [storyRef, valuesRef, tractionRef, ctaRef].forEach((r) => {
      if (r.current) observer.observe(r.current);
    });
    return () => observer.disconnect();
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  const revealUp = (key, delay = 0) => ({
    opacity: visible[key] ? 1 : 0,
    transform: visible[key] ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        "@keyframes ibFloatPulse": {
          "0%, 100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.55 },
          "50%": { transform: "translate(-50%, -52%) scale(1.06)", opacity: 0.8 },
        },
        "@keyframes ibDriftOrb": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(20px, -24px)" },
        },
      }}
    >
      <PageMeta
        title="About — InfraBlaze, Control Plane for Agent Driven Infrastructure"
        description="Built by engineers who've lived the trade-off between shipping cloud changes fast and safely. InfraBlaze is the platform we wish we had — the control plane for agent-driven infrastructure."
      />
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 64px)",
            md: "calc(56px + env(safe-area-inset-top) + 96px)",
          },
          pb: { xs: 6, md: 9 },
        }}
      >
        {/* Floating glow orbs */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1)}, transparent 60%)`,
            filter: "blur(90px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibFloatPulse 12s ease-in-out infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "8%",
            right: "-3%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.16 : 0.08)}, transparent 65%)`,
            filter: "blur(70px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibDriftOrb 15s ease-in-out infinite",
          }}
        />

        <Container
          maxWidth="md"
          sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}
        >
          <Stack spacing={3.5} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                ...fadeUp(0),
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              Cloud shouldn't feel this hard.
            </Typography>

            <Typography
              variant="h6"
              color="text.primary"
              sx={{
                ...fadeUp(160),
                maxWidth: 680,
                lineHeight: 1.7,
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" },
              }}
            >
              We started Infra
              <Box component="span" sx={{ color: "var(--amber)", fontWeight: 700 }}>Blaze</Box>
              {" "}because shipping cloud changes shouldn't be a trade-off between
              speed and safety. We've lived that trade-off. We're building the
              platform we wish we had.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ ...fadeUp(320), pt: 1 }}
            >
              <Button
                component={RouterLink}
                to="/product"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  textTransform: "none",
                  color: "common.white !important",
                  boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    color: "common.white !important",
                    transform: "translateY(-2px)",
                    boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                See the product
              </Button>
              <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                size="large"
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  textTransform: "none",
                  borderWidth: 1.5,
                  color: isDark ? "#fff" : "text.primary",
                  borderColor: alpha(isDark ? "#fff" : theme.palette.text.primary, 0.3),
                  transition: "transform 0.25s ease, border-color 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1.5,
                  },
                }}
              >
                Talk to us
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Stats strip */}
      <Box sx={{ pb: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              ...fadeUp(460),
              position: "relative",
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid var(--ink-3)",
              backgroundColor: "var(--ink-1)",
              px: { xs: 2.5, md: 3.5 },
              py: { xs: 2.5, md: 3 },
              boxShadow: "var(--glow-card)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(600px 160px at 20% 0%, color-mix(in oklab, var(--teal) 14%, transparent), transparent 60%), radial-gradient(500px 160px at 80% 100%, color-mix(in oklab, var(--teal) 10%, transparent), transparent 60%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Grid container spacing={{ xs: 2, md: 2 }}>
                {STATS.map((item, idx) => (
                  <Grid item xs={6} md={3} key={item.label}>
                    <Box
                      sx={{
                        textAlign: "center",
                        position: "relative",
                        ...(idx < STATS.length - 1 && {
                          "@media (min-width: 900px)": {
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              right: 0,
                              top: "15%",
                              bottom: "15%",
                              width: "1px",
                              background:
                                "linear-gradient(180deg, transparent, color-mix(in oklab, var(--teal) 30%, transparent), transparent)",
                            },
                          },
                        }),
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: "var(--teal)",
                          lineHeight: 1.05,
                          mb: 0.5,
                          fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.4rem" },
                          textShadow:
                            "0 0 30px color-mix(in oklab, var(--teal) 28%, transparent)",
                        }}
                      >
                        {item.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "var(--ink-7)",
                          fontWeight: 500,
                          lineHeight: 1.35,
                          px: { xs: 0.5, md: 1 },
                          fontSize: "0.82rem",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mission + Vision */}
      <Box sx={{ pb: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={3}>
            {[
              {
                label: "Our mission",
                title: "Make cloud changes safe by default.",
                body: "InfraBlaze turns intent into production-ready infrastructure with guardrails. We help teams ship faster while enforcing standards, preventing drift, and reclaiming waste — automatically.",
              },
              {
                label: "Our vision",
                title: "A control plane for multi-cloud.",
                body: "Every organization should have a unified, real-time view of assets, policies, and cost — plus an AI-powered layer that can propose and safely execute changes with full auditability.",
              },
            ].map((item, idx) => (
              <Grid item xs={12} md={6} key={item.label}>
                <Card
                  elevation={0}
                  sx={{
                    ...fadeUp(600 + idx * 100),
                    height: "100%",
                    borderRadius: "18px",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.95),
                    backdropFilter: "blur(10px)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(400px 150px at ${idx === 0 ? "0%" : "100%"} 0%, ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}, transparent 60%)`,
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: alpha(theme.palette.primary.main, 0.55),
                      boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4.5 }, position: "relative", zIndex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.8rem",
                        letterSpacing: 0.8,
                        color: "primary.main",
                        mb: 1.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: -0.4,
                        lineHeight: 1.15,
                        mb: 2,
                        fontSize: { xs: "1.5rem", md: "1.85rem" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ lineHeight: 1.85, fontSize: "0.97rem" }}
                    >
                      {item.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Origin story */}
      <Box
        ref={storyRef}
        data-key="story"
        sx={{
          py: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ ...revealUp("story"), textAlign: "center" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                mb: 3,
              }}
            >
              <FormatQuoteIcon sx={{ color: "primary.main", fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: -0.5,
                lineHeight: 1.35,
                fontSize: { xs: "1.5rem", md: "2rem" },
                color: "text.primary",
                mb: 3,
                maxWidth: 760,
                mx: "auto",
              }}
            >
              "We managed production cloud platforms at hyperscale — and watched
              great engineers burn out on drift, cost surprises, and 3am incidents
              caused by simple, reviewable changes."
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: "1.05rem",
                lineHeight: 1.85,
                maxWidth: 640,
                mx: "auto",
              }}
            >
              InfraBlaze was founded by engineers with deep experience at{" "}
              <Box component="span" sx={{ fontWeight: 800, color: "text.primary" }}>
                Amazon Web Services
              </Box>
              {" "}and enterprise-scale cloud operations at one of the world's largest
              financial institutions. We've led infrastructure deployment at scale
              and handled compliance automation — now we're turning that expertise
              into a platform every team can use.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Values / Principles */}
      <Box
        ref={valuesRef}
        data-key="values"
        sx={{
          pb: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 5, md: 7 } }}>
            <Typography
              variant="h3"
              sx={{
                ...revealUp("values"),
                fontWeight: 900,
                letterSpacing: -0.5,
                fontSize: { xs: "1.9rem", md: "2.6rem" },
              }}
            >
              What we believe
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                ...revealUp("values", 120),
                maxWidth: 640,
                lineHeight: 1.75,
                fontSize: "1.05rem",
              }}
            >
              The principles that shape every feature, workflow, and decision we make.
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {VALUES.map((value, idx) => {
              const Icon = value.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={value.title}>
                  <Box
                    sx={{
                      ...revealUp("values", 200 + idx * 80),
                      height: "100%",
                      p: { xs: 3, md: 3.5 },
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.8),
                      backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.5 : 0.92),
                      backdropFilter: "blur(10px)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      },
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                        boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1)}`,
                        "&::before": { opacity: 1 },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        mb: 2,
                      }}
                    >
                      <Icon sx={{ color: "primary.main", fontSize: 24 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        mb: 1,
                        fontSize: "1.1rem",
                        letterSpacing: -0.2,
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: "0.92rem", lineHeight: 1.7 }}
                    >
                      {value.body}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Traction highlight */}
      <Box ref={tractionRef} data-key="traction" sx={{ pb: { xs: 7, md: 9 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              ...revealUp("traction"),
              position: "relative",
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, isDark ? 0.45 : 0.3),
              backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.96),
              backdropFilter: "blur(14px)",
              p: { xs: 4, md: 5 },
              textAlign: "center",
              boxShadow: `0 30px 90px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: `radial-gradient(500px 200px at 50% 0%, ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.16)}, transparent 60%), radial-gradient(400px 200px at 50% 100%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.18 : 0.1)}, transparent 60%)`,
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    boxShadow: `0 0 12px ${theme.palette.primary.main}`,
                    animation: reducedMotion ? "none" : "ibFloatPulse 2s ease-in-out infinite",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "primary.main", letterSpacing: 0.4 }}
                >
                  LIVE · PRIVATE BETA
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: -0.5,
                  lineHeight: 1.2,
                  mb: 1.5,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Currently in beta with{" "}
                <Box component="span" sx={{ color: "primary.main" }}>
                  ~10 design partners
                </Box>
                .
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 520,
                  mx: "auto",
                  lineHeight: 1.75,
                  fontSize: "1rem",
                }}
              >
                We're shipping daily and selectively onboarding teams that want
                to help shape the product.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box ref={ctaRef} data-key="cta" sx={{ pb: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ ...revealUp("cta"), textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.5,
                lineHeight: 1.15,
                mb: 2,
                fontSize: { xs: "1.8rem", md: "2.4rem" },
              }}
            >
              Want to see Infra
              <Box component="span" sx={{ color: "var(--amber)" }}>Blaze</Box>
              {" "}in action?
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 560, mx: "auto", lineHeight: 1.75, fontSize: "1.05rem" }}
            >
              Connect your cloud accounts and explore inventory, guardrails, and
              automated workflows — in minutes.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
            >
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  textTransform: "none",
                  color: "common.white !important",
                  boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    color: "common.white !important",
                    transform: "translateY(-2px)",
                    boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                Request a demo
              </Button>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="outlined"
                size="large"
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  textTransform: "none",
                  borderWidth: 1.5,
                  color: isDark ? "#fff" : "text.primary",
                  borderColor: alpha(isDark ? "#fff" : theme.palette.text.primary, 0.3),
                  "&:hover": {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                View pricing
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
}
